import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import type { AdminCredential } from '../../api/admin.api';
import { useListAdminCredentialsQuery } from '../../api/admin.api';
import { ADMIN_NEW_CREDENTIAL_PATH } from '../../config';
import { formatDate, formatDateTime, isExpiringWithin } from '../../content/adminDates';
import { cardSurfaceSx, tokens } from '../../theme';
import { ContentState } from '../../components/ContentState';

/** A key within this many days of expiry is called out, because a repository stops syncing the night after. */
const EXPIRY_WARNING_DAYS = 14;

/** What a key is, in the words an admin knows it by. */
const kindLabel = (credential: AdminCredential): string => {
  if (credential.kind === 'github-app') {
    return `GitHub App, ID ${credential.appId}`;
  }

  return credential.kind === 'gitlab-token' ? 'GitLab access token' : 'GitHub personal access token';
};

/**
 * Every access key, **as metadata only**: who added it and when, when it expires,
 * when it was last used or last failed, and the repositories that depend on it. The
 * secret is not in the response, so there is nothing here to hide or to leak.
 *
 * **The person who added a key is shown by email because they are the point of
 * contact** when it stops working.
 */
export const CredentialsSection = () => {
  const { data, isLoading, error, refetch } = useListAdminCredentialsQuery();
  const credentials = data ?? [];

  return (
    <ContentState
      isLoading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      errorTitle="Unable to load the access keys"
      errorNoun="list of access keys"
      isEmpty={credentials.length === 0}
      emptyTitle="No access keys yet"
      emptyMessage="A public repository needs none. A private or internal one does."
    >
      <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {credentials.map((credential) => {
          const failing = credential.lastFailureAt !== undefined;
          const expiresAt = credential.kind === 'github-app' ? undefined : credential.expiresAt;
          const expiring = isExpiringWithin(expiresAt, EXPIRY_WARNING_DAYS);

          return (
            <Box component="li" key={credential.id} sx={{ ...cardSurfaceSx, padding: 5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'baseline' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {credential.label}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: tokens.fontFamily.mono }}>
                  {credential.host}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {kindLabel(credential)}
                </Typography>
                {failing && (
                  <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                    Failing
                  </Typography>
                )}
              </Box>

              <Typography variant="body2">
                Added by {credential.addedBy.name === undefined ? '' : `${credential.addedBy.name}, `}
                <a href={`mailto:${credential.addedBy.email}`}>{credential.addedBy.email}</a> on{' '}
                {formatDate(credential.addedAt) ?? credential.addedAt}. They are who to ask when it stops working.
              </Typography>

              {credential.rotatedBy !== undefined && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Replaced by {credential.rotatedBy.email} on{' '}
                  {formatDate(credential.rotatedAt) ?? credential.rotatedAt ?? ''}.
                </Typography>
              )}

              {credential.kind !== 'github-app' && (
                <Typography variant="body2" sx={expiring ? { color: 'error.main' } : undefined}>
                  {expiresAt === undefined
                    ? 'No expiry recorded.'
                    : `Expires ${formatDate(expiresAt) ?? expiresAt}${expiring ? ', which is soon or past.' : '.'}`}
                </Typography>
              )}

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {credential.lastUsedAt === undefined
                  ? 'Not used yet.'
                  : `Last used ${formatDateTime(credential.lastUsedAt) ?? credential.lastUsedAt}.`}
              </Typography>

              {failing && (
                <Typography variant="body2" role="alert" sx={{ color: 'error.main' }}>
                  Last failed {formatDateTime(credential.lastFailureAt) ?? credential.lastFailureAt}
                  {credential.lastFailureReason === undefined ? '.' : `: ${credential.lastFailureReason}`}
                </Typography>
              )}

              <Typography variant="body2">
                {credential.repos.length === 0
                  ? 'Serves no repositories.'
                  : `Serves ${credential.repos.join(', ')}.`}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </ContentState>
  );
};

/** The link that opens the add-a-key form, drawn once and used by the section's heading row. */
export const AddCredentialButton = () => (
  <Button component={RouterLink} to={ADMIN_NEW_CREDENTIAL_PATH} variant="outlined" size="small">
    Add an access key
  </Button>
);
